import * as Sentry from '@sentry/react';
import {
  selectAppReconnecting,
  selectIceRelayRequired,
} from 'features/application/applicationSlice';
import { localFeedDisconnected, localFeedUpdated } from 'features/streaming/streamingSlice';
import { LocalFeedUpdated } from 'features/streaming/types';

import { selectAllMediaDevicePermissions } from 'features/user-media/userMediaSlice';
import Janus, { JanusJS } from 'lib/janus';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { store } from 'store/store';
import { noop } from 'utils/flow';
import { logger } from 'utils/logger';
import { isError } from 'utils/types';
import { joinAsPublisherTemplate, publishTemplate, sendMessage } from 'utils/webrtc/messages';

import {
  GENERIC_JANUS_ERROR,
  JOIN_ROOM_ERROR,
  NO_PUBLISHING_FEED,
  NO_ROOM_ID,
  NO_ROOM_PIN,
} from 'utils/webrtc/errors';

import {
  FeedMediaState,
  FeedMediaStates,
  PublishingFeedStatus,
  PublishingKind,
  PublishingNegotiationMeta,
  PublishingOptions,
  PublishIntent,
  PublishOffer,
  PublishTemplateOptions,
  RTCClient,
} from 'utils/webrtc/index';

import { ControlledPublishingHandle } from 'utils/webrtc/publishing/ControlledPublishingHandle';
import { MediaController, MessageHandler } from 'utils/webrtc/publishing';
import { SignalingBus } from 'utils/webrtc/SignalingBus';
import { SelectedVirtualBackground } from 'features/virtual-backgrounds/types';
import { VirtualBackgroundManager } from 'utils/webrtc/virtual-background/VirtualBackgroundManager';
import { v4 as uuidv4 } from 'uuid';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import { handleAttachError } from 'utils/webrtc/handleAttachError';
import { buildPublishTracks } from 'utils/webrtc/publishing/buildPublishTracks';
import { ConnectionHealthMonitor } from 'utils/webrtc/publishing/ConnectionHealthMonitor';
import { VideoBroadcastReconciler } from 'utils/webrtc/publishing/VideoBroadcastReconciler';
import { VirtualBackgroundCoordinator } from 'utils/webrtc/publishing/VirtualBackgroundCoordinator';
import { setOpusFmtpParams } from 'utils/webrtc/publishing/setOpusFmtpParams';
import { logJanusHandleAttached } from 'utils/webrtc/logJanusHandleAttached';
import {
  emitPublishEvent,
  PUBLISH_DEFERRED_EVENT,
  publishDeferredMessage,
  PUBLISH_NEGOTIATION_RESET_SKIPPED_EVENT,
  publishNegotiationResetSkippedMessage,
} from 'utils/webrtc/publishing/publishTelemetry';

// Re-export for callers that import from this module (types live in webrtc/types
// to avoid a circular dependency through webrtc/index).
export type { PublishTrackOp, PublishIntent, PublishOffer } from 'utils/webrtc/index';

const negotiationMetaInitialValues: PublishingNegotiationMeta = {
  isNegotiating: false,
  pendingAudioBroadcastState: null,
  pendingAudioSignalingState: false,
  pendingHDSimulcast: null,
  pendingOffer: null,
  activeOffer: null,
  iceRestartAttempt: false,
  mediaFailureReported: false,
};

export abstract class BasePublishing {
  kind: PublishingKind;

  status: PublishingFeedStatus = PublishingFeedStatus.idle;

  handle?: ControlledPublishingHandle;

  mediaController: MediaController;

  messageHandler: MessageHandler;

  signalingBus: SignalingBus;

  negotiationMeta: PublishingNegotiationMeta;

  broadcastIntended: boolean = false;

  // Set when `requestPublish` defers for lack of a handle; cleared unconditionally at the top
  // of `attachPlugin`, scoping it to one attach cycle. Doubles as the defer timestamp so
  // `onPublisherJoinedSaga` can compute `msSinceDefer` without a second field to keep in sync.
  publishDeferredAt: number | null = null;

  // Denominator for negotiation-outcome telemetry; only the path that actually starts a
  // negotiation increments it, not the queued-replay path.
  negotiationCount: number = 0;

  defaultMediaState: FeedMediaState = {
    enabled: false,
    allowed: false,
    broadcasting: false,
    captured: false,
  };

  mediaStates = {
    video: { ...this.defaultMediaState },
    audio: { ...this.defaultMediaState },
  };

  private healthMonitor: ConnectionHealthMonitor;

  // Set by the never-established rebuild; consumed by handleCleanup, which re-attaches.
  private pendingRebuild = false;

  // Handle whose events we accept. A rebuild flips it on the fresh attach, so the wedged
  // handle's late hangup and `oncleanup` cannot re-enter the state machine against the new PC.
  private activeHandleId?: string;

  private vbCoordinator: VirtualBackgroundCoordinator;

  protected videoReconciler: VideoBroadcastReconciler;

  private disposedInternal: boolean = false;

  get disposed(): boolean {
    return this.disposedInternal;
  }

  private set disposed(value: boolean) {
    this.disposedInternal = value;
  }

  abstract getActiveMediaConfig: () => {
    video?: boolean | MediaTrackConstraints;
    audio?: boolean | MediaTrackConstraints;
    stream?: MediaStream;
  };

  abstract onMediaState: (medium: 'audio' | 'video', on: boolean, mid?: string) => Promise<void>;

  protected abstract getSimulcastMaxBitrates: () => {
    low: number;
    medium?: number;
    high: number;
  };

  protected abstract getSendEncodings: () => Array<{
    rid: string;
    active: boolean;
    maxBitrate?: number;
    scaleResolutionDownBy?: number;
  }> | null;

  protected abstract isSimulcastEnabled: () => boolean;

  protected getVideoTrackType = (): 'video' | 'screen' => 'video';

  protected constructor(kind: PublishingKind) {
    this.kind = kind;

    this.mediaController = new MediaController(this);
    this.messageHandler = new MessageHandler(this);
    this.signalingBus = new SignalingBus(this.kind);
    this.healthMonitor = new ConnectionHealthMonitor(this);
    this.videoReconciler = new VideoBroadcastReconciler(this);
    this.vbCoordinator = new VirtualBackgroundCoordinator(this, this.videoReconciler);
    this.negotiationMeta = {
      ...negotiationMetaInitialValues,
    };
  }

  attachPlugin = () => {
    // A fresh attach starts a new cycle; the defer window only opens after this point (the
    // handle is assigned in the async `success` callback below), so clearing here scopes the
    // latch to one cycle by construction instead of by case analysis over teardown paths.
    this.publishDeferredAt = null;
    this.healthMonitor.dtlsRecovery.onNewPeerConnection();

    const connection = RTCClient.mediaServerConnector.connections.publishing;

    const videoroomHandle = uuidv4();
    this.activeHandleId = videoroomHandle;

    const forActiveHandle =
      <A extends unknown[]>(handler: (...args: A) => void) =>
      (...args: A) => {
        if (this.activeHandleId === videoroomHandle) {
          handler(...args);
        }
      };

    if (this.status === PublishingFeedStatus.idle) {
      this.status = PublishingFeedStatus.connecting;
    }

    connection.janus.attach({
      plugin: 'janus.plugin.videoroom',
      opaqueId: connection.handle,
      success: async (videoroom) => {
        if (this.activeHandleId !== videoroomHandle) {
          videoroom.detach();
          return;
        }

        this.status = PublishingFeedStatus.connected;
        this.updateRedux();

        this.handle = { videoroom, handle: videoroomHandle, streams: {} };

        logJanusHandleAttached(this.kind, videoroom, connection.janus);

        await this.joinRoom();
      },
      oncleanup: forActiveHandle(this.handleCleanup),
      error: handleAttachError(`attach_publishing_feed_${videoroomHandle}`),
      consentDialog: noop,
      webrtcState: forActiveHandle((on, reason) => {
        Janus.log(`${this.kind} feed: WebRTC PeerConnection is ${on ? 'up' : 'down'} now`);

        if (on) {
          this.healthMonitor.dtlsRecovery.onWebRTCUp();
        }

        if (
          on &&
          this.kind === 'publishing' &&
          Janus.webRTCAdapter.browserDetails.browser === 'firefox'
        ) {
          this.handle?.videoroom.send({
            message: {
              request: 'configure',
              bitrate: this.getSimulcastMaxBitrates().medium,
            },
          });
        }

        if (!on) {
          this.healthMonitor.onWebRTCConnectionDown(reason);
        }
      }),
      signalingState: forActiveHandle(this.onSignalingState),
      mediaState: forActiveHandle(this.onMediaState),
      onmessage: forActiveHandle(this.messageHandler.onMessage),
      onlocaltrack: forActiveHandle(async (track, on) => {
        if (!on) {
          await this.mediaController.handleRemoveTrack(track);
          return;
        }
        this.mediaController.addStream(track);
        this.updateRedux();
      }),
      iceState: forActiveHandle(this.healthMonitor.onIceState),
      connectionState: forActiveHandle(this.healthMonitor.onConnectionState),
      iceGatheringState: forActiveHandle(this.healthMonitor.onIceGatheringState),
      // Don't consume tracks in publishing feed
      onremotetrack: () => {},
      sframe: SFrameManager.getClient(),
    });
  };

  private joinRoom = async () => {
    const { roomId, roomPin } = RTCClient;
    const feed = this.handle;

    if (!roomId) {
      throw NO_ROOM_ID();
    }
    if (!roomPin) {
      throw NO_ROOM_PIN();
    }

    if (!feed) {
      return;
    }

    try {
      await sendMessage(feed.videoroom, joinAsPublisherTemplate(roomId, roomPin));
    } catch (error) {
      if (isError(error)) {
        Sentry.captureException(error);
        throw JOIN_ROOM_ERROR(error.message);
      } else {
        throw error;
      }
    }
  };

  handleCleanup = async () => {
    this.healthMonitor.clearRestartTimer();
    this.healthMonitor.dtlsRecovery.onPeerConnectionGone();

    if (this.disposed) {
      this.pendingRebuild = false;
      this.cleanupStreams();
      return;
    }

    if (this.kind === 'publishing') {
      await VirtualBackgroundManager.deactivate();
    }

    this.cleanupStreams();

    delete this.handle;

    this.healthMonitor.resetForNewPeerConnection();

    const isReconnecting = selectAppReconnecting(store.getState());
    if (isReconnecting) {
      this.reconnectionReset();
    }

    this.updateRedux();

    logger.debug(`${this.kind} feed: cleaned up connection`);

    const rebuildPending = this.pendingRebuild;
    this.pendingRebuild = false;

    if (rebuildPending && !isReconnecting) {
      this.attachPlugin();
    } else {
      // No fresh PC is coming, so the rebuild deadline has nothing left to time out.
      this.healthMonitor.dtlsRecovery.abandonRebuild();
    }
  };

  cleanupConnection = ({ detach = false }: { detach?: boolean } = {}) => {
    logger.debug(`${this.kind} feed: cleaning up connection`);
    const feed = this.handle;

    if (!feed || RTCClient.isMediaServerError) {
      this.handleCleanup();
    }

    if (!feed) {
      return;
    }

    if (feed.feedId) {
      RTCClient.disconnectFeed(feed.feedId);
    }

    if (detach) {
      feed.videoroom.detach();
      return;
    }

    feed.videoroom.hangup();
  };

  // Status only — callers must already have torn down the connection; `hangup()` does the full teardown.
  markIdle = () => {
    this.status = PublishingFeedStatus.idle;
  };

  hangup = (stopSignalingStreams?: boolean) => {
    this.status = PublishingFeedStatus.idle;

    // A hangup can land before an in-flight rebuild's own cleanup arrives; clearing the latch
    // here stops handleCleanup from re-attaching a handle the user just asked to close.
    this.pendingRebuild = false;

    if (stopSignalingStreams) {
      this.stopSignalingStreams();
    }

    // TODO: We might need to move resetMediaStates to `cleanupConnection`
    this.resetMediaStates();
    this.cleanupConnection();
  };

  dispose = () => {
    this.disposed = true;
    this.healthMonitor.clearRestartTimer();
    this.healthMonitor.dtlsRecovery.onPeerConnectionGone();
    this.healthMonitor.dtlsRecovery.abandonRebuild();
  };

  // A latch rather than a direct re-attach: handleCleanup deletes the handle, so it is the one
  // place that can attach the successor. `this.hangup()` is avoided here — it resets mediaStates.
  rebuildOnFreshHandle = async (): Promise<boolean> => {
    // The teardown below stops this stream's tracks, and a re-offer would publish the ended ones.
    RTCClient.resetPublishOptions();

    this.pendingRebuild = true;

    // Detaching, not hanging up: a handle torn down only locally stays joined server-side, keeps
    // Janus's DTLS deadline running and holds the publisher slot the rebuilt handle needs.
    this.cleanupConnection({ detach: true });

    return true;
  };

  reconnectionReset = () => {
    this.mediaStates.video.captured = false;
    this.mediaStates.audio.captured = false;
    this.status = PublishingFeedStatus.idle;
    this.negotiationMeta = {
      ...negotiationMetaInitialValues,
    };
    this.videoReconciler.resetForReconnection();

    store.dispatch(localFeedDisconnected({ kind: this.kind }));
  };

  requestPublish = async (offerParams?: PublishOffer) => {
    if (this.disposed) {
      return;
    }

    const feed = this.handle;

    if (!feed) {
      // During the attach gap the handle is legitimately absent; the 'joined' message
      // re-drives the publish from the recorded intent.
      if (this.broadcastIntended) {
        const hasPublishingConnection = !!RTCClient.mediaServerConnector?.connections?.publishing;
        this.publishDeferredAt = performance.now();
        emitPublishEvent({
          event: PUBLISH_DEFERRED_EVENT,
          message: publishDeferredMessage('no-handle'),
          level: 'info',
          feedType: this.kind,
          visibility: 'fleet',
          cap: 10,
          attributes: {
            reason: 'no-handle',
            feedStatus: this.status,
            hasPublishingConnection,
          },
        });
        return;
      }

      throw NO_PUBLISHING_FEED(this.kind);
    }

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    // prevent attempt to publish without devices
    if (!this.getPeerConnection() && !videoEnabled && !audioEnabled) {
      RTCClient.resetPublishOptions();

      return;
    }

    const offer = await this.prepareOffer(offerParams);
    // Ungated on intent: the offer commits whatever it captured. Read here because the wire branch
    // below strips offer.intent.
    const commitsVideo = offer.intent?.video === 'send';

    // save upcoming request during existing negotiation
    if (this.negotiationMeta.isNegotiating) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('Negotiation in progress, queueing upcoming requests...', offer);

      this.negotiationMeta.pendingOffer = offer;
      this.videoReconciler.commitVideoBroadcast(commitsVideo);
      return;
    }

    this.negotiationMeta.isNegotiating = true;
    this.negotiationMeta.activeOffer = offer;
    this.negotiationCount += 1;

    // Strip the app-side intent at the janus wire boundary; the queued pendingOffer above keeps it.
    delete offer.intent;
    feed.videoroom.createOffer(offer);
    this.videoReconciler.commitVideoBroadcast(commitsVideo);
  };

  publish = async (options: PublishTemplateOptions, jsep: JanusJS.JSEP) => {
    if (!this.handle) {
      return;
    }

    await sendMessage(this.handle.videoroom, publishTemplate(options), jsep);
  };

  private onSignalingState = async (state: RTCSignalingState) => {
    logger.remote({ system: true, capture: 'streaming' }).debug('Signaling state:', state);

    if (state === 'stable' || state === 'closed') {
      this.negotiationMeta.isNegotiating = false;

      if (this.negotiationMeta.pendingOffer) {
        const pendingOffer = cloneDeep(this.negotiationMeta.pendingOffer);
        this.negotiationMeta.pendingOffer = null;

        // Always replay: prepareOffer rebuilds tracks from live transceiver presence, so a queued
        // cold-add replays as a replace and a queued remove survives.
        logger
          .remote({ system: true, capture: 'streaming' })
          .debug('Requesting negotiation with queued offer...', pendingOffer);
        await this.requestPublish(pendingOffer);
      }

      this.negotiationMeta.activeOffer = null;
    } else {
      this.negotiationMeta.isNegotiating = true;
    }
  };

  // isNegotiating clears only on a 'stable' transition, which never comes if the offer died
  // before the wire — reset here or publishing wedges permanently.
  private recoverFromFailedOffer = async () => {
    const signalingState = this.getPeerConnection()?.signalingState;
    if (signalingState && signalingState !== 'stable') {
      const hadPendingOffer = !!this.negotiationMeta.pendingOffer;
      emitPublishEvent({
        event: PUBLISH_NEGOTIATION_RESET_SKIPPED_EVENT,
        message: publishNegotiationResetSkippedMessage(signalingState),
        level: 'warn',
        feedType: this.kind,
        visibility: 'fleet',
        cap: 5,
        attributes: {
          signalingState,
          hadPendingOffer,
          negotiationCount: this.negotiationCount,
        },
      });
      return;
    }

    this.negotiationMeta.isNegotiating = false;
    this.negotiationMeta.activeOffer = null;

    const { pendingOffer } = this.negotiationMeta;
    this.negotiationMeta.pendingOffer = null;

    if (pendingOffer) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .debug('Replaying queued offer after failed negotiation...', pendingOffer);
      await this.requestPublish(cloneDeep(pendingOffer));
    }
  };

  private prepareOffer = async (offerParams?: PublishOffer) => {
    const activeMediaConfig = this.getActiveMediaConfig();

    const videoEnabled = this.mediaStates.video.enabled;
    const audioEnabled = this.mediaStates.audio.enabled;

    const params: PublishOffer = merge(
      {
        // Only a PC-creating offer reads this; a remembered relay decision must not
        // race the direct path again.
        iceTransportPolicy: selectIceRelayRequired(store.getState()) ? 'relay' : undefined,
        // Gate on the active SFrame client, not the E2EE flag: a non-E2EE room has no client, so
        // externalEncryption stays falsy and RTP keeps flowing.
        externalEncryption: !!SFrameManager.getClient(),
        // default intent = current enabled state; targeted callers override via offerParams.intent.
        // simulcast tuning rides on the video track descriptor (buildPublishTracks), not here.
        intent: {
          audio: audioEnabled ? ('send' as const) : undefined,
          video: videoEnabled ? ('send' as const) : undefined,
        },
        customizeSdp: (jsep: JanusJS.JSEP) => {
          jsep.sdp = setOpusFmtpParams(jsep.sdp, { usedtx: 1 });
        },
        error: async (error: unknown, response?: Response) => {
          GENERIC_JANUS_ERROR(`publish_local_feed`)(error, response);
          await this.recoverFromFailedOffer();
        },
      },
      offerParams
    );

    // Caller intent is authoritative: a single-kind op must not re-offer (and re-capture) the
    // other kind. Intent-less offers still derive both kinds from live state.
    if (offerParams?.intent) {
      params.intent = offerParams.intent;
    }

    // Flags follow the resolved intent, not the live enabled snapshot: a remove teardown keeps
    // enabled=true until requestPublish resolves, which would wrongly configure{video:true}.
    const intent = params.intent ?? {};
    // Omit kinds the intent doesn't mention: videoroom leaves an omitted kind's relay unchanged,
    // while an explicit false would pause the other kind at the SFU.
    params.success ??= (jsep: JanusJS.JSEP) =>
      this.publish(
        {
          ...(intent.audio !== undefined && { audio: intent.audio === 'send' }),
          ...(intent.video !== undefined && { video: intent.video === 'send' }),
        },
        jsep
      );

    const offer = cloneDeep(params);

    // ICE restart restores transport only — capture descriptors would swap a live processed
    // track (VB canvas, screenshare) for a raw re-gUM. Intent stays so configure doesn't mute.
    const keepExistingSenders = !!offerParams?.iceRestart && !!this.getPeerConnection();

    // Built after the merge so lodash.merge doesn't index-merge a replayed offer's stale tracks[].
    // offer.intent stays attached for replay — requestPublish strips it before the wire.
    offer.tracks = keepExistingSenders ? [] : this.buildPublishTracks(offer.intent ?? {});

    // The carried stream binds onto capture in-vendor.
    if (this.kind === 'publishing' && !this.getPeerConnection()) {
      if (RTCClient.publishOptions.stream) {
        offer.stream = RTCClient.publishOptions.stream;
      }
    }
    if (videoEnabled && !keepExistingSenders) {
      // Skip VB restore unless this offer re-captures video; an audio-only intent has no video
      // descriptor to consume the restored stream.
      if (
        this.kind === 'publishing' &&
        intent.video === 'send' &&
        this.vbCoordinator.shouldRestoreVirtualBackground(offer)
      ) {
        offer.stream = await this.vbCoordinator.restoreVirtualBackground();
      }
      if (this.kind === 'screensharing') {
        offer.stream = activeMediaConfig.stream;
      }
    }

    return offer;
  };

  // Host accessors are re-read on every call: the test suite swaps these members on the instance.
  private buildPublishTracks = (intent: PublishIntent): JanusJS.TrackOption[] =>
    buildPublishTracks(intent, {
      getAudioMid: this.getAudioMid,
      getVideoMid: this.getVideoMid,
      getTransceiverMid: this.getTransceiverMid,
      getVideoTrackType: this.getVideoTrackType,
      getActiveMediaConfig: this.getActiveMediaConfig,
      isSimulcastEnabled: this.isSimulcastEnabled,
      getSimulcastMaxBitrates: this.getSimulcastMaxBitrates,
      getSendEncodings: this.getSendEncodings,
    });

  startVideoBroadcasting = async () => {
    // Flip intent synchronously for instant UI feedback; actual publish work is serialized
    // through the reconcile chain for every caller (toggle, unmute, device switch, SDK).
    this.mediaStates.video.enabled = true;
    this.mediaStates.video.broadcasting = true;
    this.updateRedux();

    await this.videoReconciler.enqueueVideoReconcile();
  };

  stopVideoBroadcasting = async () => {
    this.mediaStates.video.enabled = false;
    this.mediaStates.video.broadcasting = false;
    this.mediaStates.video.captured = false;
    this.updateRedux();

    // Screenshare publishes video through its own path (never startVideoBroadcasting) and calls
    // this directly, so it has no reconcile-chain reality to converge against — apply directly.
    if (this.kind === 'screensharing') {
      await this.videoReconciler.deactivateVideoBroadcast();
      return;
    }

    await this.videoReconciler.enqueueVideoReconcile();
  };

  toggleVideoBroadcasting = async () => {
    logger
      .remote({ action: true })
      .info(
        `Video toggled by user, from ${
          this.mediaStates.video.broadcasting ? 'enabled' : 'disabled'
        } to ${this.mediaStates.video.broadcasting ? 'disabled' : 'enabled'}`
      );

    if (this.mediaStates.video.broadcasting) {
      await this.stopVideoBroadcasting();
    } else {
      await this.startVideoBroadcasting();
    }
  };

  blockVideoPublishing = async () => {
    logger.remote().log('Video publishing blocked');

    this.mediaStates.video.allowed = false;
    this.mediaStates.video.captured = false;

    if (this.mediaStates.video.enabled) {
      await this.stopVideoBroadcasting();
    } else {
      this.updateRedux();
    }
  };

  startAudioBroadcasting = async () => {
    if (this.mediaStates.audio.broadcasting) {
      return;
    }

    this.mediaStates.audio.broadcasting = true;

    if (this.handle) {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );
      this.signalingBus.addMessage('start', this.handle.feedId, audioStreams);
    }

    this.updateRedux();

    await this.updateAudioBroadcasting();
  };

  stopAudioBroadcasting = async (unpublish?: boolean) => {
    if (!this.mediaStates.audio.broadcasting) {
      return;
    }

    this.mediaStates.audio.broadcasting = false;

    if (this.handle && this.kind === 'publishing') {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );
      this.signalingBus.addMessage('stop', this.handle.feedId, audioStreams);
    }

    if (unpublish) {
      // Keep the audio m-line: hard-removing it makes the SFU drop forwarding, silencing immortal receiver subs.
      this.mediaStates.audio.enabled = false;
      // Stop the local capture ourselves — permission-revoke leaves a still-live track.
      this.stopLocalAudioTracks();
    } else {
      await this.updateAudioBroadcasting();
    }

    this.updateRedux();
  };

  toggleAudioBroadcasting = async () => {
    logger
      .remote({ action: true })
      .info(
        `Audio toggled by user, from ${
          this.mediaStates.audio.broadcasting ? 'enabled' : 'disabled'
        } to ${this.mediaStates.audio.broadcasting ? 'disabled' : 'enabled'}`
      );

    this.mediaStates.audio.broadcasting = !this.mediaStates.audio.broadcasting;

    const type = this.mediaStates.audio.broadcasting ? 'start' : 'stop';

    if (this.handle) {
      const audioStreams = Object.values(this.handle.streams).filter(
        (stream) => stream.type === 'audio'
      );

      if (audioStreams.length) {
        this.signalingBus.addMessage(type, this.handle.feedId, audioStreams);
      }
      // we've enabled audio but there's no audio stream yet.
      // remember to post an update when it appears
      else if (this.mediaStates.audio.broadcasting) {
        this.negotiationMeta.pendingAudioSignalingState = true;
      } else {
        // Same pre-media-event gap as the video stop above.
        const mid = this.getTransceiverMid('audio');
        if (mid) {
          this.signalingBus.addMessage(type, this.handle.feedId, [{ type: 'audio', mid }]);
        }
      }
    }

    this.updateRedux();
    await this.updateAudioBroadcasting();
  };

  blockAudioPublishing = async () => {
    logger.remote().log('Audio publishing blocked');

    this.mediaStates.audio.allowed = false;
    this.mediaStates.audio.captured = false;

    if (this.mediaStates.audio.enabled) {
      await this.stopAudioBroadcasting(true);
    } else {
      this.updateRedux();
    }
  };

  private getAudioSenderTrack = (): MediaStreamTrack | undefined =>
    this.getPeerConnection()
      ?.getSenders()
      .find(({ track }) => track?.kind === 'audio')?.track ?? undefined;

  private stopLocalAudioTracks = () => {
    this.getPeerConnection()
      ?.getSenders()
      .forEach(({ track }) => {
        if (track?.kind === 'audio') {
          track.stop();
        }
      });
  };

  private updateAudioBroadcasting = async () => {
    const {
      enabled: audioEnabled,
      allowed: audioAllowed,
      broadcasting: audioBroadcasting,
    } = this.mediaStates.audio;

    const senderTrack = this.getAudioSenderTrack();
    // Un-muting an ended track only enables silence (unplug-while-muted leaves one attached).
    // Gated on broadcasting: re-capturing on a *mute* would open the mic behind a muted UI.
    const unmutingDeadTrack = audioBroadcasting && senderTrack?.readyState === 'ended';
    const republish = audioAllowed && (!audioEnabled || unmutingDeadTrack);
    const audioMid = this.handle
      ? Object.values(this.handle.streams).find((stream) => stream.type === 'audio')?.mid
      : undefined;

    logger.remote({ system: true, capture: 'streaming' }).debug('Applying audio broadcast state:', {
      broadcasting: audioBroadcasting,
      enabled: audioEnabled,
      allowed: audioAllowed,
      republish,
      hasHandle: !!this.handle,
      mid: audioMid ?? null,
      senderTrack: senderTrack
        ? { enabled: senderTrack.enabled, readyState: senderTrack.readyState }
        : null,
    });

    if (republish) {
      this.mediaStates.audio.enabled = true;

      this.updateRedux();

      await this.requestPublish({ intent: { audio: 'send' } });
    } else if (this.handle) {
      // don't re-negotiate anymore to keep instant feedback and better performance
      if (audioMid) {
        // drive the mute state from the broadcasting flag, not the live track
        if (this.mediaStates.audio.broadcasting) {
          this.handle.videoroom.unmuteAudio(audioMid);
        } else {
          this.handle.videoroom.muteAudio(audioMid);
        }
      } else {
        // no mid yet, awaiting media state update
        this.negotiationMeta.pendingAudioBroadcastState = this.mediaStates.audio.broadcasting;
      }
    }
  };

  stopBroadcasting = () => {
    this.stopVideoBroadcasting();
    this.stopAudioBroadcasting();
  };

  // @TODO: refactor to handle external config changes without internal code edits.
  // The cleaner approach was reverted because it caused regression 7d2704a4fae70ba360118f9bfb7fceb0cc905b32.
  configureStreamingState = (options: PublishingOptions = {}) => {
    const mediaPermissions = selectAllMediaDevicePermissions(store.getState());

    this.mediaStates.video.allowed = mediaPermissions.videoinput === 'granted';
    this.mediaStates.audio.allowed = mediaPermissions.audioinput === 'granted';

    this.mediaStates.video.enabled = this.mediaStates.video.allowed && !!options.video;
    this.mediaStates.audio.enabled = this.mediaStates.audio.allowed && !!options.audio;

    this.mediaStates.video.broadcasting = this.mediaStates.video.enabled;
    this.mediaStates.audio.broadcasting = this.mediaStates.audio.enabled;

    this.updateRedux();
  };

  updateStreamingPermissions = () => {
    const mediaPermissions = selectAllMediaDevicePermissions(store.getState());

    this.mediaStates.audio.allowed = mediaPermissions.audioinput === 'granted';
    this.mediaStates.video.allowed = mediaPermissions.videoinput === 'granted';

    this.updateRedux();
  };

  getMediaStates = (): FeedMediaStates => ({
    audio: this.mediaStates.audio,
    video: this.mediaStates.video,
  });

  resetMediaStates = (media?: 'video' | 'audio') => {
    if (media) {
      this.mediaStates[media] = { ...this.defaultMediaState };
    } else {
      this.mediaStates.video = { ...this.defaultMediaState };
      this.mediaStates.audio = { ...this.defaultMediaState };
    }
    // An audio-only reset must not touch the video-live latch.
    if (media !== 'audio') {
      this.videoReconciler.resetLatch();
    }
  };

  restoreAudioEnabled = () => {
    this.mediaStates.audio.enabled = true;
    this.updateRedux();
  };

  // Unlike restoreAudioEnabled, this does not call updateRedux() — projecting the change is the caller's responsibility.
  disableMedia = (kind: 'video' | 'audio') => {
    this.mediaStates[kind].enabled = false;
  };

  restorePreservedMediaStates = ({
    preserveVideo,
    preserveAudio,
  }: {
    preserveVideo: boolean;
    preserveAudio: boolean;
  }) => {
    this.mediaStates.video.enabled = preserveVideo;
    this.mediaStates.audio.enabled = preserveAudio;
  };

  markBroadcastIntended = (intended: boolean) => {
    this.broadcastIntended = intended;
  };

  getStream = (id: string) => this.mediaController.streams[id];

  cleanupStreams = () => {
    Object.keys(this.mediaController.streams).forEach((id) => {
      this.mediaController.removeStream(id);
    });
  };

  stopSignalingStreams = () => {
    if (this.handle) {
      this.signalingBus.addMessage('stop', this.handle.feedId, Object.values(this.handle.streams));
    }
  };

  updateRedux = () => {
    if (this.disposed) {
      return;
    }

    const state = this.uiState;
    store.dispatch(localFeedUpdated({ kind: this.kind, data: state }));
  };

  cleanupUIStreams = () => {
    if (this.disposed) {
      return;
    }

    const currentState = this.uiState;
    const clearedStreamsState = {
      ...currentState,
      streams: [],
    };

    store.dispatch(localFeedUpdated({ kind: this.kind, data: clearedStreamsState }));
  };

  private get uiState(): LocalFeedUpdated {
    const {
      status,
      mediaStates: {
        video: { enabled: videoEnabled, broadcasting: videoBroadcasting },
        audio: { enabled: audioEnabled, broadcasting: audioBroadcasting },
      },
    } = this;

    const streams = Object.values(this.mediaController.streams).map((stream) => ({
      kind: stream.kind,
      id: stream.id,
    }));

    return {
      feedId: this.handle?.feedId,
      status,
      audioEnabled,
      videoEnabled,
      audioBroadcasting,
      videoBroadcasting,
      streams,
    };
  }

  changeVirtualBackground = (config: SelectedVirtualBackground): Promise<boolean> =>
    this.vbCoordinator.changeVirtualBackground(config);

  resetVirtualBackground = () => this.vbCoordinator.resetVirtualBackground();

  activateVirtualBackground = (
    config: SelectedVirtualBackground,
    params: { stream?: MediaStream } = {}
  ): Promise<MediaStream | undefined> =>
    this.vbCoordinator.activateVirtualBackground(config, params);

  getPeerConnection = (): RTCPeerConnection | undefined => this.handle?.videoroom?.webrtcStuff?.pc;

  // Whether a video transceiver already exists, so a re-enable can replace into it instead of
  // adding. Matches janus captureDevices' lookup (receiver.track.kind === 'video').
  hasVideoTransceiver = (): boolean =>
    this.getPeerConnection()
      ?.getTransceivers()
      .some((transceiver) => transceiver.receiver.track?.kind === 'video') ?? false;

  getVideoMid = (): string | undefined => {
    if (!this.handle) {
      return undefined;
    }
    const fromStreamMap = Object.values(this.handle.streams).find(
      (stream) => stream.type === 'video'
    )?.mid;
    return fromStreamMap ?? this.getTransceiverMid('video');
  };

  // SFU-map-only by design: do NOT add a getTransceiverMid('audio') fallback here. It would drop
  // changeAudioDevice's mute-at-join guard and let a muted mic go live on the sender. Any
  // transceiver-mid fallback belongs only at the buildPublishTracks audio branches, which already have it.
  getAudioMid = (): string | undefined => {
    if (!this.handle) {
      return undefined;
    }
    return Object.values(this.handle.streams).find((stream) => stream.type === 'audio')?.mid;
  };

  // The SFU stream map (handle.streams) isn't written until the first onMediaState, so before that
  // a replay would re-add an m-line. The negotiated transceiver already carries the mid — use it.
  private getTransceiverMid = (kind: 'audio' | 'video'): string | undefined =>
    this.getPeerConnection()
      ?.getTransceivers()
      .find((transceiver) => transceiver.receiver.track?.kind === kind)?.mid ?? undefined;
}
